/**
 * Fase 22 — Impressão via Windows (modo híbrido).
 *
 * Contexto: a Jetway JP-500 do Carlos está instalada como "impressora do Windows"
 * (driver oficial Jetway), não como porta COM virtual. A Web Serial API do Chrome
 * NÃO enxerga impressoras Windows — por isso o modo ESC/POS falhava.
 *
 * Solução: gerar HTML em 80mm com CSS dedicado e chamar window.print().
 * O driver Jetway recebe como papel 80mm normal e imprime. Funciona com QUALQUER
 * impressora térmica instalada no sistema — zero driver extra.
 *
 * Usamos IFRAME (não popup) para evitar bloqueio por navegador.
 */

const BASE_CSS = `
  @page { size: 80mm auto; margin: 0; }
  * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  html, body {
    margin: 0;
    padding: 0;
    font-family: 'Courier New', 'Consolas', monospace;
    font-size: 12px;
    color: #000;
    background: #fff;
    width: 72mm;
  }
  body { padding: 2mm 2mm 6mm; }
  .center { text-align: center; }
  .right { text-align: right; }
  .bold { font-weight: 900; }
  .big { font-size: 22px; font-weight: 900; line-height: 1.1; }
  .huge { font-size: 32px; font-weight: 900; line-height: 1.05; }
  .muted { color: #444; font-size: 10px; }
  .hr { border: none; border-top: 1px dashed #000; margin: 3mm 0; }
  .solid { border-top: 1px solid #000; }
  .row { display: flex; justify-content: space-between; align-items: baseline; gap: 6px; }
  .item { margin: 1mm 0; }
  .lh-tight { line-height: 1.2; }
  .pad-bottom { padding-bottom: 8mm; }
  .logo {
    display: block;
    margin: 0 auto 2mm;
    max-width: 50mm;
    width: 100%;
    height: auto;
    image-rendering: pixelated;
    image-rendering: -moz-crisp-edges;
    image-rendering: crisp-edges;
    filter: contrast(1.2);
  }
  .pix-block { text-align: center; margin: 3mm 0 1mm; }
  .pix-title { font-weight: 900; font-size: 13px; letter-spacing: 1px; }
  .pix-amount { font-size: 22px; font-weight: 900; margin: 1mm 0; }
  .pix-qr {
    display: block;
    margin: 1mm auto;
    width: 38mm;
    height: 38mm;
    image-rendering: pixelated;
  }
  .pix-hint { font-size: 10px; color: #000; margin-top: 1mm; }
`;

/**
 * URL absoluta da logo monocromática para impressão térmica.
 * Necessário pois srcdoc do iframe torna o base URL "about:srcdoc"
 * e quebra resoluções relativas como "/logo-thermal.png".
 */
function getLogoSrc() {
  try {
    if (typeof window !== 'undefined' && window.location && window.location.origin) {
      return `${window.location.origin}/logo-thermal.png`;
    }
  } catch {}
  return '/logo-thermal.png';
}

function logoHTML() {
  return `<img class="logo" src="${getLogoSrc()}" alt="" onerror="this.style.display='none'" />`;
}

/**
 * Bloco PIX renderizado no rodapé do cupom (extrato e delivery).
 * Recebe DataURL do QR Code já gerado (gerado no dispatcher async). Se vazio,
 * o bloco inteiro é omitido — não imprime placeholder quebrado.
 */
function pixBlockHTML(qrDataURL, amount) {
  if (!qrDataURL) return '';
  const amountTxt = amount != null ? money(amount) : '';
  return `
    <hr class="hr solid" />
    <div class="pix-block">
      <div class="pix-title">PAGUE COM PIX</div>
      ${amountTxt ? `<div class="pix-amount">${amountTxt}</div>` : ''}
      <img class="pix-qr" src="${qrDataURL}" alt="QR Pix" />
      <div class="pix-hint">Aponte a câmera do app do banco</div>
    </div>
  `;
}

/**
 * Injeta um HTML num iframe oculto e dispara window.print().
 * Retorna Promise que resolve quando o diálogo de impressão é fechado.
 *
 * Fase 26 — Bug fix: usar srcdoc em vez de doc.write().
 * Antes, `appendChild(iframe)` disparava `onload` imediatamente com about:blank
 * (iframe vazio) e o `w.print()` imprimia folha em branco. Só a 2ª chamada
 * saía correta porque o conteúdo já tinha sido escrito. Com srcdoc, o browser
 * espera o HTML estar totalmente carregado antes de disparar onload.
 */
export function printHTML(bodyHTML, title = 'Cupom') {
  return new Promise((resolve, reject) => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return reject(new Error('Ambiente sem document'));
    }

    const fullHtml = `<!doctype html><html><head><meta charset="utf-8"><title>${escapeHtml(title)}</title><style>${BASE_CSS}</style></head><body>${bodyHTML}</body></html>`;

    const iframe = document.createElement('iframe');
    iframe.setAttribute('aria-hidden', 'true');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    iframe.style.visibility = 'hidden';

    const cleanup = () => {
      try { document.body.removeChild(iframe); } catch {}
    };

    let printed = false;

    iframe.onload = () => {
      // srcdoc garantiu que o HTML foi totalmente carregado antes deste evento.
      if (printed) return;
      printed = true;
      try {
        const w = iframe.contentWindow;
        if (!w) {
          cleanup();
          return reject(new Error('iframe sem contentWindow'));
        }
        const done = () => {
          setTimeout(cleanup, 300);
          resolve({ ok: true });
        };
        w.addEventListener('afterprint', done, { once: true });
        // Fallback: se afterprint não vier (Safari/outros), limpa em 10s.
        setTimeout(() => { cleanup(); resolve({ ok: true, fallback: true }); }, 10000);
        // Aguarda imagens (logo) terminarem de carregar antes de imprimir.
        const triggerPrint = () => {
          try {
            w.focus();
            w.print();
          } catch (err) {
            cleanup();
            reject(err);
          }
        };
        const waitImages = () => {
          try {
            const imgs = Array.from(w.document.images || []);
            if (imgs.length === 0) return triggerPrint();
            const pending = imgs.filter((img) => !img.complete);
            if (pending.length === 0) return triggerPrint();
            let remaining = pending.length;
            const onSettle = () => {
              remaining -= 1;
              if (remaining <= 0) triggerPrint();
            };
            pending.forEach((img) => {
              img.addEventListener('load', onSettle, { once: true });
              img.addEventListener('error', onSettle, { once: true });
            });
            // Timeout de segurança caso imagem trave.
            setTimeout(triggerPrint, 1500);
          } catch {
            triggerPrint();
          }
        };
        // Pequeno delay para renderização inicial de fontes.
        setTimeout(waitImages, 80);
      } catch (err) {
        cleanup();
        reject(err);
      }
    };

    iframe.onerror = (err) => {
      cleanup();
      reject(err);
    };

    // srcdoc é crucial: adia o onload até o HTML estar renderizado.
    iframe.srcdoc = fullHtml;
    document.body.appendChild(iframe);
  });
}

function escapeHtml(s) {
  return String(s ?? '').replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  }[c]));
}

const money = (v) => `R$ ${Number(v || 0).toFixed(2).replace('.', ',')}`;

// ─── HTML TEMPLATES ──────────────────────────────────────────────

export function commandTicketHTML(command, lastEntry, businessName = 'Kaven Restaurante') {
  const hasWeight = lastEntry && lastEntry.weight > 0;
  const history = Array.isArray(command.weightEntries) && command.weightEntries.length > 1
    ? command.weightEntries.map((e) => `<div class="row lh-tight"><span>${Number(e.weight).toFixed(3)}kg × ${Number(e.pricePerKg).toFixed(2)}</span><span class="bold">${money(e.subtotal)}</span></div>`).join('')
    : '';
  return `
    ${logoHTML()}
    <div class="center bold">${escapeHtml(businessName.toUpperCase())}</div>
    <div class="center">CUPOM DE COMANDA</div>
    <hr class="hr solid" />
    <div class="center huge">COMANDA ${escapeHtml(String(command.number))}</div>
    <div class="center muted">${new Date().toLocaleString('pt-BR')}</div>
    <hr class="hr" />
    ${hasWeight ? `
      <div class="big">PESO: ${Number(lastEntry.weight).toFixed(3)} kg</div>
      <div class="big">R$/kg: ${Number(lastEntry.pricePerKg).toFixed(2)}</div>
      <hr class="hr" />
    ` : ''}
    ${history ? `<div class="bold">Histórico:</div>${history}<hr class="hr" />` : ''}
    ${Number(command.buffetTotal || 0) > 0 ? `<div class="row"><span>Buffet:</span><span>${money(command.buffetTotal)}</span></div>` : ''}
    ${Number(command.itemsTotal || 0) > 0 ? `<div class="row"><span>Bebidas/Itens:</span><span>${money(command.itemsTotal)}</span></div>` : ''}
    <div class="row big" style="margin-top:3mm"><span>TOTAL:</span><span>${money(command.total)}</span></div>
    <hr class="hr" />
    <div class="center muted">*** NÃO É DOCUMENTO FISCAL ***</div>
    <div class="pad-bottom"></div>
  `;
}

export function commandReceiptHTML(command, businessName = 'Kaven Restaurante', pixQRDataURL = '') {
  const entries = Array.isArray(command.weightEntries) ? command.weightEntries : [];
  const items = Array.isArray(command.items) ? command.items : [];
  const finalTotal = command.finalTotal || command.total;
  const showPix = pixQRDataURL && (!command.paymentMethod || /pix/i.test(String(command.paymentMethod)));
  return `
    ${logoHTML()}
    <div class="center bold">${escapeHtml(businessName.toUpperCase())}</div>
    <div class="center">EXTRATO DE FECHAMENTO</div>
    <hr class="hr solid" />
    <div class="center huge">COMANDA ${escapeHtml(String(command.number))}</div>
    <div class="center muted">${new Date().toLocaleString('pt-BR')}</div>
    <hr class="hr" />
    ${entries.length ? `<div class="bold">Buffet:</div>${entries.map((e) => `<div class="row lh-tight"><span>${Number(e.weight).toFixed(3)}kg × ${Number(e.pricePerKg).toFixed(2)}</span><span>${money(e.subtotal)}</span></div>`).join('')}<hr class="hr" />` : ''}
    ${items.length ? `<div class="bold">Bebidas/Itens:</div>${items.map((i) => `<div class="row lh-tight"><span>${Number(i.quantity || 1)}× ${escapeHtml(i.name || '')}</span><span>${money(i.subtotal || i.price * (i.quantity || 1))}</span></div>`).join('')}<hr class="hr" />` : ''}
    <div class="row"><span>Subtotal:</span><span>${money(command.total)}</span></div>
    ${command.serviceCharge ? `<div class="row"><span>Serviço 10%:</span><span>${money(command.serviceCharge)}</span></div>` : ''}
    <div class="row big" style="margin-top:3mm"><span>TOTAL:</span><span>${money(finalTotal)}</span></div>
    ${command.paymentMethod ? `<div class="row muted" style="margin-top:2mm"><span>Pagamento:</span><span>${escapeHtml(command.paymentMethod)}</span></div>` : ''}
    ${command.amountReceived != null ? `<div class="row muted"><span>Recebido:</span><span>${money(command.amountReceived)}</span></div>` : ''}
    ${command.changeGiven != null ? `<div class="row muted"><span>Troco:</span><span>${money(command.changeGiven)}</span></div>` : ''}
    ${showPix ? pixBlockHTML(pixQRDataURL, finalTotal) : ''}
    <hr class="hr" />
    <div class="center">Obrigado pela preferência!</div>
    <div class="center muted">*** NÃO É DOCUMENTO FISCAL ***</div>
    <div class="pad-bottom"></div>
  `;
}

export function kitchenLabelHTML(order) {
  const items = Array.isArray(order.items) ? order.items : [];
  return `
    ${logoHTML()}
    <div class="center bold big">COZINHA</div>
    <hr class="hr solid" />
    <div class="center huge">#${escapeHtml(String(order.id || '?'))}</div>
    <div class="center bold">${escapeHtml(order.customerName || 'Cliente')}</div>
    <div class="center muted">${new Date().toLocaleString('pt-BR')}</div>
    <hr class="hr" />
    ${items.map((i) => `
      <div class="item">
        <div class="bold big">${Number(i.quantity || 1)}× ${escapeHtml(i.name || '')}</div>
        ${i.notes ? `<div class="muted">>> ${escapeHtml(i.notes)}</div>` : ''}
      </div>
    `).join('')}
    <hr class="hr" />
    ${order.neighborhood ? `<div class="muted center">Bairro: ${escapeHtml(order.neighborhood)}</div>` : ''}
    <div class="pad-bottom"></div>
  `;
}

export function deliveryLabelHTML(order, pixQRDataURL = '') {
  const items = Array.isArray(order.items) ? order.items : [];
  const showPix = pixQRDataURL && /pix/i.test(String(order.paymentMethod || 'Pix'));
  return `
    ${logoHTML()}
    <div class="center bold">DELIVERY</div>
    <hr class="hr solid" />
    <div class="center huge">#${escapeHtml(String(order.id || '?'))}</div>
    <hr class="hr" />
    <div class="bold big">${escapeHtml(order.customerName || 'Cliente')}</div>
    ${order.customerPhone ? `<div>${escapeHtml(order.customerPhone)}</div>` : ''}
    <hr class="hr" />
    <div class="bold">${escapeHtml(order.neighborhood || '')}</div>
    ${order.address ? `<div>${escapeHtml(order.address)}</div>` : ''}
    ${order.landmark ? `<div class="muted">Ref: ${escapeHtml(order.landmark)}</div>` : ''}
    <hr class="hr" />
    ${items.map((i) => `<div class="row lh-tight"><span>${Number(i.quantity || 1)}× ${escapeHtml(i.name || '')}</span><span>${money(i.total || i.price * (i.quantity || 1))}</span></div>`).join('')}
    <hr class="hr" />
    <div class="row big"><span>TOTAL:</span><span>${money(order.total)}</span></div>
    <div class="row muted"><span>Pagamento:</span><span>${escapeHtml(order.paymentMethod || 'Pix')}</span></div>
    ${showPix ? pixBlockHTML(pixQRDataURL, order.total) : ''}
    <div class="pad-bottom"></div>
  `;
}

/**
 * v7.24 — Cupom de Fechamento Flash (caixa diário).
 * Imprime resumo do dia: total bruto, qtd. comandas, ticket médio,
 * total do buffet por kg, breakdown por forma de pagamento, top itens.
 *
 * Recebe o objeto `report` retornado por `buildDailyCloseReport()` +
 * o nome do operador que disparou o fechamento.
 */
export function dailyCloseHTML(report, operatorName = 'Operador') {
  if (!report) return '<div class="center">Relatório vazio.</div>';
  const dateBR = report.date instanceof Date
    ? report.date.toLocaleDateString('pt-BR')
    : new Date(report.date || Date.now()).toLocaleDateString('pt-BR');
  const ticketAvg = report.totalCommands > 0
    ? report.totalRevenue / report.totalCommands
    : 0;
  const buffetItem = (report.topItems || []).find((i) => /buffet/i.test(i.name));
  const buffetTotal = buffetItem ? buffetItem.total : 0;
  const kgSold = Number(report.totalKgSold || 0);
  // breakdown forma de pagamento
  const methods = Object.entries(report.byMethod || {})
    .filter(([, v]) => v.total > 0)
    .map(([name, v]) => ({ name, total: v.total, count: v.count }));
  const topItems = (report.topItems || []).slice(0, 5);

  return `
    ${logoHTML()}
    <div class="center bold">KAVEN RESTAURANTE</div>
    <div class="center">FECHAMENTO FLASH</div>
    <hr class="hr solid" />
    <div class="center big">${escapeHtml(dateBR)}</div>
    <div class="center muted">Operador: ${escapeHtml(operatorName)}</div>
    <div class="center muted">Emitido: ${new Date().toLocaleTimeString('pt-BR')}</div>
    <hr class="hr" />

    <div class="bold">RESUMO DO DIA</div>
    <div class="row"><span>Total bruto:</span><span class="bold">${money(report.totalRevenue)}</span></div>
    <div class="row"><span>Qtd. comandas:</span><span>${report.totalCommands}</span></div>
    <div class="row"><span>Ticket médio:</span><span>${money(ticketAvg)}</span></div>
    ${report.totalService > 0 ? `<div class="row muted"><span>Serviço 10%:</span><span>${money(report.totalService)}</span></div>` : ''}
    <hr class="hr" />

    ${kgSold > 0 || buffetTotal > 0 ? `
      <div class="bold">BUFFET POR QUILO</div>
      ${kgSold > 0 ? `<div class="row"><span>Peso vendido:</span><span>${kgSold.toFixed(3).replace('.', ',')} kg</span></div>` : ''}
      ${buffetTotal > 0 ? `<div class="row"><span>Faturado balança:</span><span class="bold">${money(buffetTotal)}</span></div>` : ''}
      <hr class="hr" />
    ` : ''}

    ${methods.length ? `
      <div class="bold">PAGAMENTOS</div>
      ${methods.map((m) => `<div class="row lh-tight"><span>${escapeHtml(m.name)} (${m.count})</span><span>${money(m.total)}</span></div>`).join('')}
      <hr class="hr" />
    ` : ''}

    ${topItems.length ? `
      <div class="bold">TOP ITENS</div>
      ${topItems.map((i) => `<div class="row lh-tight"><span>${Number(i.count)}× ${escapeHtml(i.name)}</span><span>${money(i.total)}</span></div>`).join('')}
      <hr class="hr" />
    ` : ''}

    <div class="center muted">*** NÃO É DOCUMENTO FISCAL ***</div>
    <div class="center muted">Use apenas para conferência de caixa.</div>
    <div class="pad-bottom"></div>
  `;
}

export function testPrintHTML() {
  return `
    ${logoHTML()}
    <div class="center bold big">KAVEN RESTAURANTE</div>
    <div class="center">TESTE DE IMPRESSÃO</div>
    <hr class="hr solid" />
    <div class="center huge">OK ✓</div>
    <hr class="hr" />
    <div>Data: ${new Date().toLocaleString('pt-BR')}</div>
    <div>Modo: Windows (via Sistema)</div>
    <div>Largura: 80mm</div>
    <hr class="hr" />
    <div class="center muted">Se este cupom saiu legível, a impressão Windows está funcionando.</div>
    <div class="pad-bottom"></div>
  `;
}
